import { useCallback } from 'react';
import { isSelectedByKey } from '../../display/selectorDisplay';
import type { AsyncSelectorMode } from '../AsyncSelector.types';

export interface UseAsyncSelectorSelectionOptions<TItem> {
  mode: AsyncSelectorMode;
  selected: readonly TItem[];
  getKey: (item: TItem) => string | number;
  onChange: (next: readonly TItem[]) => void;
  isItemSelected?: (item: TItem, selected: readonly TItem[]) => boolean;
  resetSuggestions: () => void;
  closePopover: () => void;
}

export function useAsyncSelectorSelection<TItem>({
  mode,
  selected,
  getKey,
  onChange,
  isItemSelected,
  resetSuggestions,
  closePopover,
}: UseAsyncSelectorSelectionOptions<TItem>) {
  const isSelected = useCallback(
    (item: TItem) => {
      if (isItemSelected) return isItemSelected(item, selected);
      return isSelectedByKey(getKey)(item, selected);
    },
    [isItemSelected, selected, getKey]
  );

  const handleSelect = useCallback(
    (item: TItem) => {
      if (isSelected(item)) return;
      if (mode === 'single') {
        onChange([item]);
        resetSuggestions();
        closePopover();
        return;
      }
      onChange([...selected, item]);
      resetSuggestions();
    },
    [isSelected, mode, onChange, selected, resetSuggestions, closePopover]
  );

  const handleToggleSelection = useCallback(
    (item: TItem) => {
      if (mode === 'single') {
        handleSelect(item);
        return;
      }
      const key = getKey(item);
      if (isSelected(item)) {
        onChange(selected.filter((existing) => getKey(existing) !== key));
      } else {
        onChange([...selected, item]);
      }
    },
    [mode, handleSelect, getKey, isSelected, onChange, selected]
  );

  const handleRemove = useCallback(
    (item: TItem) => {
      const key = getKey(item);
      onChange(selected.filter((existing) => getKey(existing) !== key));
    },
    [getKey, onChange, selected]
  );

  const handleClear = useCallback(() => {
    onChange([]);
    resetSuggestions();
  }, [onChange, resetSuggestions]);

  return {
    isSelected,
    handleSelect,
    handleToggleSelection,
    handleRemove,
    handleClear,
  };
}
